import apiService from "@/lib/api";
import { clearTenantScope } from "@/lib/tenantScope";
import { clearAllDrafts } from "@/lib/draftStorage";
import { clearAccessToken, getAccessToken, setAccessToken } from "@/lib/tokenSession";

const AUTH_V2 = "/api/v2/auth";
const OAUTH_STATE_KEY = "oauth_state";

// The one in-flight refresh, shared by every caller. See AuthService.refresh.
let refreshInFlight = null;

const normalizeAuthResponse = (data = {}) => {
  const payload = data?.data || data;
  const accessToken = payload.access_token || payload.accessToken || "";

  return {
    ...payload,
    accessToken,
  };
};

const saveAccessToken = (token) => {
  if (token) setAccessToken(token);
};

const saveOAuthState = (state) => {
  if (state) {
    sessionStorage.setItem(OAUTH_STATE_KEY, state);
  }
};

const getOAuthState = () => sessionStorage.getItem(OAUTH_STATE_KEY);

const clearOAuthState = () => sessionStorage.removeItem(OAUTH_STATE_KEY);

const clearAuthStorage = () => {
  clearAccessToken();
  clearOAuthState();
  // A SuperAdmin's working tenant must not outlive their session — the next
  // account to sign in on this browser would otherwise start scoped to it.
  clearTenantScope();
  // Same reason, and it matters more: an unsent draft is a frank remark about a
  // named candidate, and the next account to sign in on this tab must not find
  // it waiting in a composer.
  clearAllDrafts();
};

const handleTokenResponse = (response) => {
  const normalized = normalizeAuthResponse(response.data);
  saveAccessToken(normalized.accessToken);
  return normalized;
};

const startOAuth = async (provider) => {
  const response = await apiService.get(`${AUTH_V2}/${provider}/authorize`);
  const { authorization_url, state } = response.data || {};

  if (!authorization_url || !state) {
    throw new Error("Unable to start OAuth flow. Please try again.");
  }

  saveOAuthState(state);
  window.location.assign(authorization_url);
};

const handleOAuthCallback = async ({ provider, code, stateFromProvider, storedState }) => {
  if (!code) {
    throw new Error("Authorization code missing. Please try signing in again.");
  }

  if (!storedState) {
    throw new Error("OAuth state missing. Please try signing in again.");
  }

  const response = await apiService.get(`${AUTH_V2}/${provider}/callback`, {
    params: {
      code,
      state: stateFromProvider,
      stored_state: storedState,
    },
  });

  const normalized = handleTokenResponse(response);
  clearOAuthState();

  return normalized;
};

export const AuthService = {
  getAccessToken,
  getOAuthState,
  clearAuthStorage,

  startGoogleOAuth: () => startOAuth("google"),
  startMicrosoftOAuth: () => startOAuth("microsoft"),
  startGithubOAuth: () => startOAuth("github"),
  startLinkedinOAuth: () => startOAuth("linkedin"),

  handleGoogleCallback: (code, stateFromGoogle, storedState = getOAuthState()) =>
    handleOAuthCallback({
      provider: "google",
      code,
      stateFromProvider: stateFromGoogle,
      storedState,
    }),

  handleMicrosoftCallback: (code, stateFromMicrosoft, storedState = getOAuthState()) =>
    handleOAuthCallback({
      provider: "microsoft",
      code,
      stateFromProvider: stateFromMicrosoft,
      storedState,
    }),

  handleGithubCallback: (code, stateFromGithub, storedState = getOAuthState()) =>
    handleOAuthCallback({
      provider: "github",
      code,
      stateFromProvider: stateFromGithub,
      storedState,
    }),

  handleLinkedinCallback: (code, stateFromLinkedin, storedState = getOAuthState()) =>
    handleOAuthCallback({
      provider: "linkedin",
      code,
      stateFromProvider: stateFromLinkedin,
      storedState,
    }),

  signup: async ({ name, email, password }) => {
    // Pending, not live: the server files the request for an administrator
    // and returns no tokens, so there is nothing to store here.
    const response = await apiService.post(`${AUTH_V2}/signup`, {
      name,
      email,
      password,
    });

    return response.data;
  },

  signinLocal: async ({ email, password, acknowledged }) => {
    const response = await apiService.post(`${AUTH_V2}/signin`, {
      email,
      password,
      acknowledged: acknowledged || false,
    });

    return handleTokenResponse(response);
  },

  forgotPassword: async ({ email }) => {
    const response = await apiService.post(`${AUTH_V2}/password/forgot`, { email });
    return response.data;
  },

  resetPassword: async (payload) => {
    try {
      const response = await apiService.post(`${AUTH_V2}/password/reset`, payload);
      handleTokenResponse(response);
      return response.data

    } catch (error) {
      console.error('Error while fetching list:', error);
      throw error;
    }
  },

  currentUser: async () => {
    const response = await apiService.getSecure(`${AUTH_V2}/current-user`);
    return response.data;
  },

  updateProfile: async ({ name }) => {
    const response = await apiService.patch(`${AUTH_V2}/profile`, { name });
    return response.data;
  },

  login: async (employeeId, password, acknowledged) => {
    const response = await apiService.post(`${AUTH_V2}/login`, {
      employee_id: employeeId,
      password,
      acknowledged: acknowledged || false,
    });

    return handleTokenResponse(response);
  },

  /**
   * Renew the session from the httpOnly refresh cookie.
   *
   * Single-flight: concurrent callers share one in-flight request. Several
   * things ask for a renewal independently — the 401 interceptor, the
   * proactive timer, the tab-focus check, bootstrap — and without this they
   * would each fire their own POST. The server tolerates that now, but making
   * four requests to do one job is still wrong.
   *
   * The current access token rides along in `Authorization` so the server can
   * carry an AD user's `ou` / `groups` claims into the replacement token. It
   * is not the credential — the cookie is — and it is sent even when expired,
   * which is why this uses `postSecure` rather than treating a 401 here as
   * something to retry.
   */
  refresh: () => {
    if (!refreshInFlight) {
      refreshInFlight = apiService
        .postSecure(`${AUTH_V2}/refresh`, {}, { skipAuthRefresh: true })
        .then(handleTokenResponse)
        .finally(() => {
          refreshInFlight = null;
        });
    }
    return refreshInFlight;
  },

  /**
   * End the session. The server answers 200 for an unknown or already-revoked
   * cookie, so the only failure worth reporting here is the network being
   * down — and local state is cleared either way, because a user who asked to
   * sign out must not be left holding a token.
   */
  logout: async () => {
    try {
      const response = await apiService.post(`${AUTH_V2}/logout`, {}, { skipAuthRefresh: true });
      return response.data;
    } finally {
      clearAuthStorage();
    }
  },

  changePassword: async (currentPassword, newPassword,confirmPassword) => {
    const payload = {
      currentPassword, newPassword,confirmNewPassword: confirmPassword
    }
    try {
      const response = await apiService.postSecure(`${AUTH_V2}/password/change`, payload);
      return response.data;
    } catch (error) {
      console.error('Error while changing the password:', error);
      throw error;
    }
  },

  validateInvite: async (token) => {
    if (!token) throw new Error("Invite token is missing");

    try {
      const response = await apiService.get(`${AUTH_V2}/invite/validate`, {
        params: { token },
      });
      return response.data;
    } catch (error) {
      console.error('Error while validating token:', error);
      throw error;
    }
  },

  acceptInvite: async (payload) => {
    try {
      const response = await apiService.post(`${AUTH_V2}/invite/accept`, payload);
      handleTokenResponse(response);
      return response.data

    } catch (error) {
      console.error('Error while fetching list:', error);
      throw error;
    }
  },
};

export default AuthService;
